<script lang="ts" module>
	import subscriptionAllEspresso from "@assets/images/subscription-all-espresso.jpg?w=490&format=webp&imagetools";
	import subscriptionAllEspressoMd from "@assets/images/subscription-all-espresso.jpg?w=667&format=webp&imagetools";
	import subscriptionAllFilter from "@assets/images/subscription-all-filter.jpg?w=490&format=webp&imagetools";
	import subscriptionAllFilterMd from "@assets/images/subscription-all-filter.jpg?w=667&format=webp&imagetools";
	import subscriptionEspresso from "@assets/images/subscription-espresso.jpg?w=490&format=webp&imagetools";
	import subscriptionEspressoMd from "@assets/images/subscription-espresso.jpg?w=667&format=webp&imagetools";
	import subscriptionFilter from "@assets/images/subscription-filter.jpg?w=490&format=webp&imagetools";
	import subscriptionFilterMd from "@assets/images/subscription-filter.jpg?w=667&format=webp&imagetools";
	import SubcategoryButton from "@components/SubcategoryButton.svelte";
	import SubscriptionCard from "@components/SubscriptionCard.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import ProductB2bListingGrid from "@components/product/ProductB2bListingGrid.svelte";
	import ProductB2cListingGrid from "@components/product/ProductB2cListingGrid.svelte";
	import SubscriptionSection from "@components/sections/SubscriptionSection.svelte";
	import TipSection from "@components/sections/TipSection.svelte";
	import { createTitle } from "@utils/createTitle.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { BreadcrumbList, CollectionPage, ListItem } from "schema-dts";
	import type { ClassValue } from "svelte/elements";
	import cofeeBeanIcon from "@core/assets/icons/cofee-bean.svg?raw";
	import type { Category } from "@core/schema/Category.js";
	import { CategoryType } from "@core/schema/CategoryType.js";
	import { CustomerType } from "@core/schema/CustomerType.js";
	import type { SourceSetQuery } from "@core/schema/SourceSetQuery.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { SubscriptionVariant } from "@core/schema/SubscriptionVariant.js";
	import { appName } from "@core/schema/names/appName.js";
	import { categoryTypeNameMap } from "@core/schema/names/categoryTypeNameMap.js";
	import { getCategoryPath } from "@core/schema/paths/getCategoryPath.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { getSubcategoryPath } from "@core/schema/paths/getSubcategoryPath.js";
	import { findSubcategoryOfSubscriptionVariant } from "@core/schema/utils/findSubcategoryOfSubscriptionVariant.js";
	import { getCustomerType } from "@core/schema/utils/getCustomerType.js";
	import { subcategoryDescriptionByPath } from "@core/schema/utils/subcategoryDescriptionByPath.js";
	import { subcategoryTipsByPath } from "@core/schema/utils/subcategoryTipsByPath.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import capsulesImageMobile from "../assets/images/tips/capsules.jpg?webp&quality=89&h=700&imagetools";
	import capsulesImage from "../assets/images/tips/capsules.jpg?webp&quality=90&imagetools";
	import dripbagImageMobile from "../assets/images/tips/dripbag.jpg?webp&quality=89&h=700&imagetools";
	import dripbagImage from "../assets/images/tips/dripbag.jpg?webp&quality=90&imagetools";
	import espressoImageMobile from "../assets/images/tips/espresso.jpg?webp&quality=89&h=700&imagetools";
	import espressoImage from "../assets/images/tips/espresso.jpg?webp&quality=90&imagetools";
	import filterImageMobile from "../assets/images/tips/filter.jpg?webp&quality=89&h=700&imagetools";
	import filterImage from "../assets/images/tips/filter.jpg?webp&quality=90&imagetools";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./ProductsListing.translations.json" with { type: "json" };
	import type { ProductsListingProps } from "./ProductsListingProps.js";

	const tipImageByPath: Record<
		string,
		Partial<Record<SourceSetQuery, string>> & {
			default: string;
			media?: ClassValue;
			align?: "left" | "center" | "right";
		}
	> = {
		"/kava/filtr": {
			default: filterImageMobile,
			">md": filterImage,
			media: "sm:object-[50%_40%] opacity-[.6]",
			align: "left",
		},
		"/en/coffee/filter": {
			default: filterImageMobile,
			">md": filterImage,
			media: "sm:object-[50%_40%] opacity-[.6]",
			align: "left",
		},
		"/kava/espresso": {
			default: espressoImageMobile,
			">md": espressoImage,
			media: "sm:object-[50%_45%]",
			align: "right",
		},
		"/en/coffee/espresso": {
			default: espressoImageMobile,
			">md": espressoImage,
			media: "sm:object-[50%_45%]",
			align: "right",
		},
		"/kava/kapsle": {
			default: capsulesImageMobile,
			">md": capsulesImage,
			media: "sm:object-[50%_42.5%] opacity-[.76]",
			align: "left",
		},
		"/en/coffee/capsules": {
			default: capsulesImageMobile,
			">md": capsulesImage,
			media: "sm:object-[50%_42.5%] opacity-[.76]",
			align: "left",
		},
		"/kava/dripbagy": {
			default: dripbagImageMobile,
			">md": dripbagImage,
			align: "right",
			media: "sm:object-[50%_53%] opacity-[.64]",
		},
		"/en/coffee/dripbags": {
			default: dripbagImageMobile,
			">md": dripbagImage,
			align: "right",
			media: "sm:object-[50%_53%] opacity-[.64]",
		},
	};

	const hideAfterATipWithInsufficientProductsClass = tw(
		"[.b2c-listing:has(.tip-section):not(:has(article.product-card:nth-of-type(3)))~&]:hidden",
		"md:[.b2c-listing:has(.tip-section):not(:has(article.product-card:nth-of-type(5)))~&]:hidden",
		"lg:[.b2c-listing:has(.tip-section):not(:has(article.product-card:nth-of-type(7)))~&]:hidden",
		"2xl:[.b2c-listing:has(.tip-section):not(:has(article.product-card:nth-of-type(9)))~&]:hidden",
	);

	const subscriptionImageByVariant: Record<SubscriptionVariant, { default: string; md: string }> = {
		[SubscriptionVariant.Espresso]: { default: subscriptionEspresso, md: subscriptionEspressoMd },
		[SubscriptionVariant.Filter]: { default: subscriptionFilter, md: subscriptionFilterMd },
	};

	const allCoffeeSubscriptionCards: { variant: SubscriptionVariant; image: { default: string; md: string } }[] = [
		{
			variant: SubscriptionVariant.Espresso,
			image: { default: subscriptionEspresso, md: subscriptionEspressoMd },
		},
		{
			variant: SubscriptionVariant.Filter,
			image: { default: subscriptionAllFilter, md: subscriptionAllFilterMd },
		},
		{
			variant: SubscriptionVariant.Espresso,
			image: { default: subscriptionAllEspresso, md: subscriptionAllEspressoMd },
		},
	];
</script>

<script lang="ts">
	const translate = getTranslate(translations);

	const {
		categories,
		pricingLevel,
		products,
		selectedCategory: defaultSelectedCategory,
		selectedSubcategory: defaultSelectedSubategory,
		subcategories,
		tenant,
	}: ProductsListingProps = $props();

	const { WEB_URL_OR_LOCAL } = getGlobalSettings();
	const { description, pagePath } = getGlobalTemplateProps();

	const canonicalPath = $derived(tenant.locale in pagePath ? pagePath[tenant.locale] : undefined);
	const canonicalUrl = $derived(canonicalPath !== undefined ? `${WEB_URL_OR_LOCAL}${canonicalPath}` : undefined);
	const defaultSelectedCategoryData = $derived(categories.find(({ type }) => type === defaultSelectedCategory)?.data);

	const pageTitle = $derived.by(() => {
		if (defaultSelectedSubategory) {
			return defaultSelectedSubategory.name[tenant.locale];
		}
		return (defaultSelectedCategoryData?.name ?? categoryTypeNameMap[defaultSelectedCategory])[tenant.locale];
	});

	const collectionPageSchema = $derived<CollectionPage>({
		"@type": "CollectionPage",
		name: pageTitle,
		description,
		...(canonicalUrl !== undefined ? { url: canonicalUrl } : {}),
	});

	const breadcrumbListSchema = $derived.by<BreadcrumbList>(() => {
		const homePath = getHomepagePath(tenant);
		const categoryPath = getCategoryPath(tenant.locale, defaultSelectedCategory);
		const categoryName = categoryTypeNameMap[defaultSelectedCategory][tenant.locale];

		const items: ListItem[] = [
			{ "@type": "ListItem", position: 1, name: appName, item: `${WEB_URL_OR_LOCAL}${homePath}` },
			{ "@type": "ListItem", position: 2, name: categoryName, item: `${WEB_URL_OR_LOCAL}${categoryPath}` },
		];

		if (defaultSelectedSubategory !== undefined) {
			const subcategoryPath = getSubcategoryPath(tenant.locale, defaultSelectedSubategory);
			items.push({
				"@type": "ListItem",
				position: 3,
				name: defaultSelectedSubategory.name[tenant.locale],
				item: `${WEB_URL_OR_LOCAL}${subcategoryPath}`,
			});
		}

		return {
			"@type": "BreadcrumbList",
			itemListElement: items,
		};
	});

	let selectedCategory = $derived(defaultSelectedCategory);
	let selectedSubcategory = $derived(defaultSelectedSubategory);
	const selectedSubscriptionVariant = $derived.by(() => {
		if (!selectedSubcategory) {
			return undefined;
		}
		const subcategories = [selectedSubcategory];
		return Object.values(SubscriptionVariant).find(
			(variant) => findSubcategoryOfSubscriptionVariant(subcategories, variant, false) !== undefined,
		);
	});

	const visibleSubcategories = $derived(
		subcategories.filter((subcategory) => subcategory.category === selectedCategory),
	);
	const visibleProducts = $derived(
		products.filter((product) => {
			if (selectedSubcategory) {
				return product.subcategoryId === selectedSubcategory.id;
			}
			return visibleSubcategories.some((subcategory) => subcategory.id === product.subcategoryId);
		}),
	);

	function setActiveCategory(category: Category, pushToHistory: boolean): void {
		selectedSubcategory = undefined;
		selectedCategory = category.type;

		document.title = createTitle(
			category.data?.name?.[tenant.locale] ?? categoryTypeNameMap[category.type][tenant.locale],
		);

		if (pushToHistory) {
			history.pushState({ selectedCategory }, "", getCategoryPath(tenant.locale, category.type));
		}
	}

	function setActiveSubcategory(subcategory: SubcategoryForCustomer, pushToHistory: boolean): void {
		selectedCategory = subcategory.category;
		selectedSubcategory = subcategory;

		document.title = createTitle(subcategory.name[tenant.locale]);
		if (pushToHistory) {
			history.pushState(
				{
					selectedCategory,
					selectedSubcategory: subcategory.id,
				},
				"",
				getSubcategoryPath(tenant.locale, subcategory),
			);
		}
	}

	function handleHistoryPop(event: PopStateEvent): void {
		const state = event.state as unknown;
		if (typeof state !== "object" || state === null) {
			return;
		}
		if ("selectedSubcategory" in state && typeof state.selectedSubcategory === "string") {
			const subcategory = subcategories.find((subcategory) => subcategory.id === state.selectedSubcategory);
			if (subcategory) {
				setActiveSubcategory(subcategory, false);
			}
		} else if ("selectedCategory" in state && isEnum(CategoryType)(state.selectedCategory)) {
			const categoryData = categories.find((category) => category.type === state.selectedCategory)?.data;
			setActiveCategory({ type: state.selectedCategory, data: categoryData }, false);
		}
	}

	const customerType = $derived(
		getCustomerType({
			pricingLevel,
			// Layout doesn't differ for B2b and B2f customers in this template
			isCustomerB2f: false,
		}),
	);
</script>

<svelte:window onpopstate={handleHistoryPop} />

<StructuredData data={collectionPageSchema} />
<StructuredData data={breadcrumbListSchema} />

<Layout pageHasMobileBottomBar={customerType === CustomerType.B2b}>
	<div class="grid-container full-width gap-y-6 md:gap-y-12 mt-10 md:mt-14 mb-10">
		<div class="flex items-start justify-center gap-4 md:gap-12 w-full overflow-x-auto">
			{#each categories as category (category.type)}
				<div class="flex flex-col items-center">
					<button
						class={[
							category.type === selectedCategory ? "text-fcrdarkgray" : "text-fcrgray hover:text-fcrdarkgray",
							"font-antipol text-5.5/tighter md:text-10/tighter font-medium transition-colors",
						]}
						onclick={(): void => {
							setActiveCategory(category, true);
						}}
					>
						<Formatted
							text={category.data?.name?.[tenant.locale] ?? categoryTypeNameMap[category.type][tenant.locale]}
						/>
					</button>
					{#if category.type === selectedCategory}
						<div class="w-5 h-1 rounded-full bg-fcrbrown"></div>
					{/if}
				</div>
			{/each}
		</div>
		{#if visibleSubcategories.length > 1}
			<div class="flex items-center gap-2 text-nowrap overflow-x-auto md:flex-wrap md:justify-center p-1">
				<SubcategoryButton
					active={selectedSubcategory === undefined}
					onclick={(): void => {
						const categoryData = categories.find(({ type }) => type === selectedCategory)?.data;
						setActiveCategory({ type: selectedCategory, data: categoryData }, true);
					}}
					label={translate("all")}
					icon={cofeeBeanIcon}
				/>
				{#each visibleSubcategories as subcategory (subcategory.id)}
					<SubcategoryButton
						active={selectedSubcategory?.id === subcategory.id}
						onclick={(): void => {
							setActiveSubcategory(subcategory, true);
						}}
						label={subcategory.name[tenant.locale]}
						icon={subcategory.icon}
					/>
				{/each}
			</div>
		{/if}
	</div>
	{#if customerType === CustomerType.B2c}
		{#if selectedSubcategory}
			{@const subcategoryPath = getSubcategoryPath(tenant.locale, selectedSubcategory)}
			{@const subcategoryDescription = subcategoryDescriptionByPath[subcategoryPath]}
			{@const { media, align, ...sources } = tipImageByPath[subcategoryPath] ?? {}}
			{@const tip = subcategoryTipsByPath[subcategoryPath]}
			<ProductB2cListingGrid products={visibleProducts} {subcategories}>
				{#if selectedSubscriptionVariant}
					<SubscriptionCard
						variant={selectedSubscriptionVariant}
						image={subscriptionImageByVariant[selectedSubscriptionVariant]}
						restrictSelectionsToVariant
					/>
				{/if}
				{#if subcategoryDescription}
					<div class="col-span-full flex flex-col gap-4 max-w-[62ch] -mb-11 sm:-mb-7">
						<Heading
							level={1}
							class="font-antipol text-2xl/none sm:text-3.5xl/none"
							text={selectedSubcategory.name[tenant.locale]}
						/>
						<p class="text-fcrgray text-base sm:text-lg/normal">
							<Formatted text={subcategoryDescription} />
						</p>
					</div>
				{/if}
				{#if "default" in sources && tip}
					<TipSection heading={translate("ourTip")} {tip} {sources} {media} {align} class="[grid-row:4]" />
				{/if}
			</ProductB2cListingGrid>
		{:else}
			<ProductB2cListingGrid products={visibleProducts} {subcategories}>
				{#if selectedCategory === CategoryType.Coffee}
					{#each allCoffeeSubscriptionCards as card, cardIndex (cardIndex)}
						<SubscriptionCard variant={card.variant} image={card.image} class="subscription-card-{cardIndex + 1}" />
					{/each}
				{/if}
			</ProductB2cListingGrid>
		{/if}
	{:else}
		<ProductB2bListingGrid products={visibleProducts} {subcategories} />
	{/if}
	<div class={["h-10", hideAfterATipWithInsufficientProductsClass]}></div>
	{#if customerType === CustomerType.B2c}
		<SubscriptionSection class={hideAfterATipWithInsufficientProductsClass} />
	{/if}
</Layout>
